import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import {
  useFeedbackPeriods, useEvaluatorApprovalApi, useEmployeeNames,
  NOMINATION_ROLES, EXTERNAL_NOMINATION_ROLES, employeeAsync,
  isValidEmail, externalDisplayName,
} from './config';
import DataTable from '../../components/ui/DataTable';
import OptionsMenu from '../../components/ui/OptionsMenu';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import GenericSelect from '../../components/ui/GenericSelect';
import AsyncGenericSelect from '../../components/ui/AsyncGenericSelect';
import CheckboxInput from '../../components/ui/CheckboxInput';
import TextInput from '../../components/ui/TextInput.jsx';
import Button from '../../components/ui/Button';
import { parseTableParams, formatDate } from '../../utils/Utils';

// Task 28 — yönetici/İK 360° değerlendirici onay kuyruğu: bekleyen
// (status=="proposed") önerileri onaylar/reddeder (POST .../manager-decision)
// veya doğrudan bir değerlendirici ekler (POST .../manager-add — direkt
// "approved", min-3 kuralına tabi DEĞİL, bkz. backend
// ApplyNominationsAsync yorumu). GET v1/evaluator-nominations satırlarında
// isim YOK — employeeId/evaluatorEmployeeId, useEmployeeNames ile lazy
// çözülür (bkz. config/index.js).
const Container = styled.div``;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${(p) => (p.$open ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  z-index: 1300;
  padding: 24px;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 8px;
  max-width: 560px;
  width: 100%;
  padding: 28px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  max-height: 90vh;
  overflow-y: auto;
`;

const CardTitle = styled.h3`
  margin: 0 0 20px;
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const FieldGroup = styled.div`
  margin-bottom: 16px;
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
`;

// P1 — harici satır rozeti (MyEvaluatorsPage.ExternalTag ile aynı görsel dil).
const ExternalTag = styled.span`
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  background: #f3e8ff;
  color: #7c3aed;
  margin-left: 6px;
`;

const EMPTY_ADD_FORM = {
  periodId: null, employeeId: null, evaluatorEmployeeId: null, evaluatorRole: 'peer',
  isExternal: false, externalEmail: '', externalFullName: '',
};

export default function EvaluatorApprovalPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const { data, fetching, pagination, refresh, decide, managerAdd } = useEvaluatorApprovalApi(queryParams);

  // Harici satırlarda evaluatorEmployeeId Employee DEĞİL (öneride boş Guid) —
  // isim çözümüne sokulmaz, ad/e-posta satırın kendi alanlarından gösterilir.
  const nameIds = useMemo(() => data.flatMap((r) => (r.isExternal ? [r.employeeId] : [r.employeeId, r.evaluatorEmployeeId])), [data]);
  const employeeNames = useEmployeeNames(nameIds);

  const { periods } = useFeedbackPeriods();
  const periodOptions = useMemo(() => periods.map((p) => ({
    id: p.id,
    name: `${p.name} (${t(`performance.period.status.${p.status}`, { defaultValue: p.status })})`,
  })), [periods, t]);
  const roleOptions = useMemo(() => NOMINATION_ROLES.map((r) => ({
    id: r, name: t(`performance.feedback360.role.${r}`),
  })), [t]);
  const externalRoleOptions = useMemo(() => EXTERNAL_NOMINATION_ROLES.map((r) => ({
    id: r, name: t(`performance.feedback360.role.${r}`),
  })), [t]);

  // ── Onay / Red ──────────────────────────────────────────────────────────
  const [decisionDialog, setDecisionDialog] = useState({ open: false, row: null, decision: null });
  const [deciding, setDeciding] = useState(false);

  const openDecision = useCallback((row, decision) => setDecisionDialog({ open: true, row, decision }), []);
  const closeDecision = useCallback(() => setDecisionDialog({ open: false, row: null, decision: null }), []);

  const handleDecisionConfirm = useCallback(async () => {
    if (!decisionDialog.row) return;
    setDeciding(true);
    const ok = await decide(decisionDialog.row.id, decisionDialog.decision);
    setDeciding(false);
    if (ok) {
      closeDecision();
      refresh();
    }
  }, [decisionDialog, decide, closeDecision, refresh]);

  // ── Değerlendirici Ekle ────────────────────────────────────────────────
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addForm, setAddForm] = useState(EMPTY_ADD_FORM);
  const [adding, setAdding] = useState(false);

  const openAddModal = useCallback(() => {
    setAddForm({ ...EMPTY_ADD_FORM, periodId: periods[0]?.id || null });
    setAddModalOpen(true);
  }, [periods]);

  const handleAddSubmit = useCallback(async () => {
    setAdding(true);
    const ok = await managerAdd(addForm.isExternal
      ? {
        periodId: addForm.periodId,
        employeeId: addForm.employeeId,
        evaluatorRole: addForm.evaluatorRole,
        isExternal: true,
        externalEmail: addForm.externalEmail.trim(),
        externalFullName: addForm.externalFullName.trim(),
      }
      : {
        periodId: addForm.periodId,
        employeeId: addForm.employeeId,
        evaluatorEmployeeId: addForm.evaluatorEmployeeId,
        evaluatorRole: addForm.evaluatorRole,
      });
    setAdding(false);
    if (ok) {
      setAddModalOpen(false);
      refresh();
    }
  }, [addForm, managerAdd, refresh]);

  // Harici modda değerlendirici seçimi yerine ad+e-posta zorunlu (rol yalnız
  // peer|senior — toggle'da subordinate ise peer'a düşürülür).
  const canAddSubmit = addForm.periodId && addForm.employeeId && addForm.evaluatorRole
    && (addForm.isExternal
      ? (isValidEmail(addForm.externalEmail) && addForm.externalFullName.trim())
      : addForm.evaluatorEmployeeId);

  const handleExternalToggle = useCallback((checked) => {
    setAddForm((f) => ({
      ...f,
      isExternal: checked,
      evaluatorEmployeeId: checked ? null : f.evaluatorEmployeeId,
      evaluatorRole: checked && !EXTERNAL_NOMINATION_ROLES.includes(f.evaluatorRole) ? 'peer' : f.evaluatorRole,
    }));
  }, []);

  // ── Kolonlar ────────────────────────────────────────────────────────────
  const baseColumns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'employeeId',
      label: t('performance.feedback360.fields.employee'),
      options: { filter: false, sort: false, customBodyRender: (v) => employeeNames[v] || '...' },
    },
    {
      name: 'evaluatorEmployeeId',
      label: t('performance.feedback360.fields.evaluator'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          if (row?.isExternal) {
            return (
              <span>
                {externalDisplayName(row)}
                <ExternalTag>{t('performance.feedback360.external')}</ExternalTag>
              </span>
            );
          }
          return employeeNames[v] || '...';
        },
      },
    },
    {
      name: 'evaluatorRole',
      label: t('performance.feedback360.fields.role'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => t(`performance.feedback360.role.${v}`, { defaultValue: v }),
      },
    },
    {
      name: 'addedByManager',
      label: t('performance.feedback360.fields.source'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => (v
          ? t('performance.feedback360.sourceManager')
          : t('performance.feedback360.sourceSelf')),
      },
    },
    {
      name: 'createDate',
      label: t('performance.common.createDate'),
      options: { filter: false, sort: false, customBodyRender: (v) => formatDate(v, lang) },
    },
    // data: harici satır gösterimi (evaluator kolonu) satır nesnesine bakar.
  ], [t, employeeNames, lang, data]);

  const columns = useMemo(() => [
    ...baseColumns,
    {
      name: 'rowActions',
      label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          if (!row) return null;
          const actions = [
            { type: 'approve', label: t('performance.feedback360.approve'), onClick: () => openDecision(row, 'approve') },
            { type: 'reject', label: t('performance.feedback360.reject'), onClick: () => openDecision(row, 'reject') },
          ];
          return <OptionsMenu actions={actions} row={row} />;
        },
      },
    },
  ], [baseColumns, data, t, openDecision]);

  const handleDataStateChange = useCallback((_action, tableState) => {
    setFilterList(tableState.filterList);
    setQueryParams(parseTableParams(tableState, baseColumns));
  }, [baseColumns]);

  return (
    <Container>
      <ConfirmDialog
        isOpen={decisionDialog.open}
        title={decisionDialog.decision === 'approve'
          ? t('performance.feedback360.approveConfirmTitle')
          : t('performance.feedback360.rejectConfirmTitle')}
        message={decisionDialog.decision === 'approve'
          ? t('performance.feedback360.approveConfirmMessage')
          : t('performance.feedback360.rejectConfirmMessage')}
        type={decisionDialog.decision === 'reject' ? 'danger' : 'info'}
        confirmText={decisionDialog.decision === 'approve' ? t('performance.feedback360.approve') : t('performance.feedback360.reject')}
        onConfirm={handleDecisionConfirm}
        onCancel={closeDecision}
        loading={deciding}
      />

      <Overlay $open={addModalOpen}>
        {addModalOpen && (
          <Card>
            <CardTitle>{t('performance.feedback360.addEvaluator')}</CardTitle>

            <FieldGroup>
              <GenericSelect
                label={t('performance.goal.selectPeriod')}
                value={addForm.periodId}
                onChange={(v) => setAddForm((f) => ({ ...f, periodId: v }))}
                options={periodOptions}
                idKey="id"
                labelKey="name"
                required
              />
            </FieldGroup>

            <FieldGroup>
              <AsyncGenericSelect
                label={t('performance.feedback360.fields.employee')}
                value={addForm.employeeId}
                onChange={(v) => setAddForm((f) => ({ ...f, employeeId: v }))}
                onSearch={employeeAsync.onSearch}
                onGetById={employeeAsync.onGetById}
                required
              />
            </FieldGroup>

            <FieldGroup>
              <CheckboxInput
                label={t('performance.feedback360.isExternal')}
                checked={addForm.isExternal}
                onChange={handleExternalToggle}
              />
            </FieldGroup>

            {!addForm.isExternal && (
              <FieldGroup>
                <AsyncGenericSelect
                  label={t('performance.feedback360.fields.evaluator')}
                  value={addForm.evaluatorEmployeeId}
                  onChange={(v) => setAddForm((f) => ({ ...f, evaluatorEmployeeId: v }))}
                  onSearch={employeeAsync.onSearch}
                  onGetById={employeeAsync.onGetById}
                  required
                />
              </FieldGroup>
            )}

            {addForm.isExternal && (
              <>
                <FieldGroup>
                  <TextInput
                    label={t('performance.feedback360.externalFullName')}
                    value={addForm.externalFullName}
                    onChange={(e) => setAddForm((f) => ({ ...f, externalFullName: e.target.value }))}
                    required
                  />
                </FieldGroup>
                <FieldGroup>
                  <TextInput
                    label={t('performance.feedback360.externalEmail')}
                    type="email"
                    value={addForm.externalEmail}
                    onChange={(e) => setAddForm((f) => ({ ...f, externalEmail: e.target.value }))}
                    required
                  />
                </FieldGroup>
              </>
            )}

            <FieldGroup>
              <GenericSelect
                label={t('performance.feedback360.fields.role')}
                value={addForm.evaluatorRole}
                onChange={(v) => setAddForm((f) => ({ ...f, evaluatorRole: v }))}
                options={addForm.isExternal ? externalRoleOptions : roleOptions}
                required
              />
            </FieldGroup>

            <Actions>
              <Button variant="outline" onClick={() => setAddModalOpen(false)}>{t('common.cancel')}</Button>
              <Button onClick={handleAddSubmit} disabled={!canAddSubmit || adding} loading={adding}>
                {t('common.save')}
              </Button>
            </Actions>
          </Card>
        )}
      </Overlay>

      <DataTable
        title={t('performance.feedback360.evaluatorApprovals.title')}
        fetching={fetching}
        columns={columns}
        data={data}
        totalRowCount={pagination.totalRowCount}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        canCreateNewRecord
        newButtonLabel={t('performance.feedback360.addEvaluator')}
        onNewButtonClick={openAddModal}
        filterList={filterList}
        onFilterConfirm={setFilterList}
        onTableChanged={handleDataStateChange}
      />
    </Container>
  );
}
